import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import BrandingHeader from './components/BrandingHeader';
import InvitationHeader from './components/InvitationHeader';
import AccountCreationForm from './components/AccountCreationForm';
import OnboardingForm from './components/OnboardingForm';
import Icon from '../../components/AppIcon';

const InviteSignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [currentStep, setCurrentStep] = useState('account');
  const [loading, setLoading] = useState(false);
  const [invitationData, setInvitationData] = useState({
    valid: false,
    email: '',
    role: '',
    token: ''
  });

  useEffect(() => {
    const token = searchParams?.get('token');
    const email = searchParams?.get('email');
    const role = searchParams?.get('role');

    if (token && email && role) {
      setInvitationData({
        valid: true,
        email: email,
        role: role,
        token: token
      });
    } else {
      setInvitationData({
        valid: false,
        email: '',
        role: '',
        token: ''
      });
    }
  }, [searchParams]);

  const handleAccountCreation = async (accountData) => {
    setLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Account created:', {
        email: accountData?.email,
        token: invitationData?.token
      });
      
      setCurrentStep('onboarding');
    } catch (error) {
      console.error('Account creation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async (onboardingData) => {
    setLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Onboarding completed:', {
        ...onboardingData,
        email: invitationData?.email
      });
      
      navigate('/providers-dashboard');
    } catch (error) {
      console.error('Onboarding failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-xl">
        <BrandingHeader />

        <div className="bg-card border border-border rounded-2xl shadow-lg p-6 md:p-8 lg:p-10">
          <InvitationHeader
            invitationValid={invitationData?.valid}
            invitedRole={invitationData?.role}
            invitedEmail={invitationData?.email}
          />

          {invitationData?.valid && (
            <>
              <div className="mb-6 md:mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-250 ${
                      currentStep === 'account' ?'bg-primary text-primary-foreground' :'bg-success text-success-foreground'
                    }`}>
                      {currentStep === 'onboarding' ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <span className="text-sm md:text-base font-semibold">1</span>
                      )}
                    </div>
                    <span className={`text-sm md:text-base font-medium ${
                      currentStep === 'account' ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      Create Account
                    </span>
                  </div>

                  <div className="flex-1 h-0.5 bg-border mx-4" />

                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-250 ${
                      currentStep === 'onboarding' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      <span className="text-sm md:text-base font-semibold">2</span>
                    </div>
                    <span className={`text-sm md:text-base font-medium ${
                      currentStep === 'onboarding' ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      Profile Setup
                    </span>
                  </div>
                </div>
              </div>

              {currentStep === 'account' && (
                <AccountCreationForm
                  invitedEmail={invitationData?.email}
                  onSubmit={handleAccountCreation}
                  loading={loading}
                />
              )}

              {currentStep === 'onboarding' && (
                <OnboardingForm
                  onSubmit={handleOnboardingComplete}
                  loading={loading}
                  invitedRole={invitationData?.role}
                />
              )}
            </>
          )}

          {!invitationData?.valid && (
            <div className="text-center pt-4">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm md:text-base font-medium transition-colors duration-250"
                style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
              >
                <Icon name="ArrowLeft" size={16} />
                Return to Login
              </Link>
            </div>
          )}
        </div>

        <div className="text-center mt-6 md:mt-8">
          <p className="text-xs md:text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium transition-colors duration-250"
              style={{ color: 'var(--corvid-background-color, rgba(var(--bg, var(--color_17, 37, 99, 235)), var(--alpha-bg, 1)))' }}
            >
              Sign in here
            </Link>
          </p>
        </div>

        <div className="text-center mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date()?.getFullYear()} Videra Surgical. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default InviteSignup;